import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '../store/useAppStore';
import { qk } from '../services/queryKeys';
import { cancelEmployeeInviteApi, fetchEmployees, fetchDepartments, resolveAvatarSrc } from '../services/api';
import { listInvitations, resendInvitation, cancelInvitation } from '../services/auth';
import Avatar from '../components/Avatar';
import Chip from '../components/Chip';
import Button from '../components/Button';
import Icon from '../components/Icon';
import type { EmploymentType, EmployeeStatus } from '../types';

const STATUS_VARIANT: Record<EmployeeStatus, 'success' | 'warn' | 'neutral'> = {
  active: 'success', on_leave: 'warn', separated: 'neutral',
};
const TYPE_LABEL: Record<EmploymentType, string> = {
  full_time: 'Full-time', part_time: 'Part-time', contract: 'Contract', intern: 'Intern',
};

export default function Directory() {
  const { role, orgId, token, openModal, toast } = useAppStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');

  const { data: employees = [] } = useQuery({ queryKey: qk.employees(), queryFn: fetchEmployees });
  const { data: departments = [] } = useQuery({ queryKey: qk.departments(), queryFn: fetchDepartments });

  // HR only — fetch pending invitations to show inline on employee cards
  const { data: pendingInvites = [] } = useQuery({
    queryKey: qk.pendingInvitations(orgId),
    queryFn: () => listInvitations(orgId, token ?? ''),
    enabled: role === 'hr' && !!orgId && !!token,
    retry: false,
  });

  // Build a lookup: email (lowercase) → invitation id
  const inviteByEmail = Object.fromEntries(pendingInvites.map((i) => [i.email.toLowerCase(), i.id]));

  // Invitations with no matching employee record — otherwise invisible/unmanageable,
  // since the per-card "Invite pending" badge only renders on a matching employee card.
  const employeeEmails = new Set(
    employees.flatMap((e) => [e.email?.toLowerCase(), e.invitedEmail?.toLowerCase()]).filter(Boolean),
  );
  const unmatchedInvites = pendingInvites.filter((i) => !employeeEmails.has(i.email.toLowerCase()));

  const resendMutation = useMutation({
    mutationFn: (invitationId: string) => resendInvitation(orgId, invitationId, token ?? ''),
    onSuccess: () => {
      toast('Invitation resent');
      queryClient.invalidateQueries({ queryKey: qk.pendingInvitations(orgId) });
    },
    onError: () => toast('Failed to resend invitation', 'danger'),
  });

  const cancelMutation = useMutation({
    // Revoking also clears People's record of the invite, so that address can be
    // invited for another employee and an accepted stale invite links nothing.
    mutationFn: async ({ invitationId, employeeId }: { invitationId: string; employeeId?: string }) => {
      await cancelInvitation(orgId, invitationId, token ?? '');
      if (employeeId) await cancelEmployeeInviteApi(employeeId);
    },
    onSuccess: () => {
      toast('Invitation revoked');
      queryClient.invalidateQueries({ queryKey: qk.pendingInvitations(orgId) });
      queryClient.invalidateQueries({ queryKey: qk.employees() });
    },
    onError: () => toast('Failed to revoke invitation', 'danger'),
  });

  const filtered = employees.filter((e) => {
    const name = `${e.firstName} ${e.lastName} ${e.email} ${e.jobTitle}`.toLowerCase();
    if (search && !name.includes(search.toLowerCase())) return false;
    if (deptFilter && e.departmentId !== deptFilter) return false;
    if (typeFilter && e.employmentType !== typeFilter) return false;
    if (statusFilter && e.status !== statusFilter) return false;
    return true;
  });

  return (
    <div>
      <div className="sk-page-header">
        <h1>Directory</h1>
        {role === 'hr' && (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="outline" onClick={() => openModal('invite_member')}>
              <Icon name="mail" size={14} color="var(--accent)" />
              Invite
            </Button>
            <Button variant="accent" onClick={() => openModal('add_employee')}>
              <Icon name="plus" size={14} color="white" />
              Add Employee
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Icon name="search" size={14} color="var(--ink-3)" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' } as React.CSSProperties} />
          <input
            className="t-input"
            placeholder="Search name, email, role…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ paddingLeft: 32 }}
          />
        </div>
        <select className="t-input" style={{ width: 160 }} value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}>
          <option value="">All departments</option>
          {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select className="t-input" style={{ width: 140 }} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">All types</option>
          {(['full_time', 'part_time', 'contract', 'intern'] as EmploymentType[]).map((t) => (
            <option key={t} value={t}>{TYPE_LABEL[t]}</option>
          ))}
        </select>
        <select className="t-input" style={{ width: 130 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="on_leave">On leave</option>
          <option value="separated">Separated</option>
        </select>
        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>{filtered.length} employees</span>
      </div>

      {/* Pending invitations with no employee record yet */}
      {role === 'hr' && unmatchedInvites.length > 0 && (
        <div style={{ marginBottom: 20, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', background: 'var(--warn-soft, #fef3c7)', fontSize: 12, fontWeight: 600, color: 'var(--warn, #d97706)' }}>
            Pending invitations
          </div>
          {unmatchedInvites.map((invite) => (
            <div
              key={invite.id}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 14px', borderTop: '1px solid var(--border)',
              }}
            >
              <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink)' }}>{invite.email}</span>
              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  style={{ fontSize: 12, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 4, fontWeight: 500 }}
                  onClick={() => resendMutation.mutate(invite.id)}
                  disabled={resendMutation.isPending}
                >
                  Resend
                </button>
                <button
                  style={{ fontSize: 12, color: 'var(--ink-3)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 4, fontWeight: 500 }}
                  onClick={() => cancelMutation.mutate({ invitationId: invite.id })}
                  disabled={cancelMutation.isPending}
                >
                  Revoke
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Grid */}
      <div className="emp-grid">
        {filtered.map((emp) => {
          const dept = departments.find((d) => d.id === emp.departmentId);
          // The address HR invited for this record wins over its contact email.
          const inviteEmail = (emp.invitedEmail ?? emp.email)?.toLowerCase();
          const inviteId = inviteEmail ? inviteByEmail[inviteEmail] : undefined;
          return (
            <div key={emp.id} className="sk-emp-card" onClick={() => navigate(`/profile/${emp.id}`)}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Avatar firstName={emp.firstName} lastName={emp.lastName} src={resolveAvatarSrc(emp.avatarUrl)} color={emp.avatarColor} size="lg" />
                <Chip variant={STATUS_VARIANT[emp.status]} dot>{emp.status.replace('_', ' ')}</Chip>
              </div>
              <div className="emp-name">{emp.firstName} {emp.lastName}</div>
              <div className="emp-role">{emp.jobTitle}</div>
              <div className="emp-dept" style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <Chip variant="neutral">{dept?.name ?? '—'}</Chip>
                <Chip variant="neutral">{TYPE_LABEL[emp.employmentType]}</Chip>
                {/*
                  * CC-001 CH-U1. A record with no platformUserId is a real
                  * employee who has not signed in yet — since CH-1 removed
                  * employment self-seed, that is now a NORMAL state for anyone
                  * HR adds ahead of their first login, not an error. Quiet chip,
                  * because it is information for HR rather than a problem to fix.
                  */}
                {!emp.platformUserId && (
                  <Chip variant="neutral">not yet linked</Chip>
                )}
              </div>

              {/* Invite pending badge — HR only */}
              {inviteId && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    marginTop: 10, padding: '6px 10px', borderRadius: 8,
                    background: 'var(--warn-soft, #fef3c7)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
                  }}
                >
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--warn, #d97706)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Icon name="mail" size={11} color="var(--warn, #d97706)" />
                    Invite pending
                  </span>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      style={{ fontSize: 11, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', borderRadius: 4, fontWeight: 500 }}
                      onClick={() => resendMutation.mutate(inviteId)}
                      disabled={resendMutation.isPending}
                    >
                      Resend
                    </button>
                    <button
                      style={{ fontSize: 11, color: 'var(--ink-3)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', borderRadius: 4, fontWeight: 500 }}
                      onClick={() => cancelMutation.mutate({ invitationId: inviteId, employeeId: emp.invitedEmail ? emp.id : undefined })}
                      disabled={cancelMutation.isPending}
                    >
                      Revoke
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--ink-3)' }}>
          <Icon name="search" size={32} color="var(--border-2)" />
          <p style={{ marginTop: 12, fontSize: 'var(--fs-md)' }}>No employees match your filters</p>
        </div>
      )}
    </div>
  );
}
